<template>
  <VRow v-if="fetchStatus.isFetching">
    <VCol cols="12">
      <Preloader />
    </VCol>
  </VRow>

  <VRow v-else-if="fetchStatus.fetchError">
    <VCol cols="12">
      <VAlert type="error">
        {{ candidates.fetchError }}
      </VAlert>
    </VCol>
  </VRow>

  <VRow v-else align="center">
    <VCol
      v-if="$vuetify.breakpoint.mdAndUp"
      cols="12"
      class="py-0 px-3"
    >
      <CandidateSlider
        v-if="candidates && showNeighbours"
        :candidates="candidates"
        :candidate="candidate"
        :is-loading-more="isLoadingMore"
      />
    </VCol>
  </VRow>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import CandidateSlider from './CandidateSlider.vue';
import Preloader from '../Preloader.vue';
import Candidate from '../../orm/models/Candidate/Candidate';
import useKanbanPagination from '@/compositions/useKanbanPagination';

export default {
  name: 'CandidateFooter',
  components: { Preloader, CandidateSlider },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    candidate: {
      type: Object,
      required: true,
    },
    showNeighbours: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup() {
    const { hasMoreData, isLoadingNextPage, prefetchNextPage } = useKanbanPagination();
    return { hasMoreData, isLoadingNextPage, prefetchNextPage };
  },
  data() {
    return {
      frozenNeighbourIds: null,
      isLoadingMore: false,
    };
  },
  computed: {
    ...mapGetters('candidatePage', ['getFrozenNeighbourIds', 'getPrefetchedAtLength']),
    fetchStatus() {
      return Candidate.getFetchStatus(`all_by_vacancy_${this.vacancy.id}`);
    },
    candidates() {
      if (!this.frozenNeighbourIds || !this.frozenNeighbourIds.length) {
        return null;
      }
      return this.frozenNeighbourIds
        .map((id) => Candidate.find(id))
        .filter(Boolean);
    },
  },
  watch: {
    'candidate.id'() {
      const isNavigatingWithinList =
        this.frozenNeighbourIds && this.frozenNeighbourIds.includes(this.candidate.id);
      if (isNavigatingWithinList) return;
      this.frozenNeighbourIds = null;
      this.fetchCandidates();
    },
  },
  mounted() {
    const storedIds = this.getFrozenNeighbourIds(this.vacancy?.id);
    const isInStoredList = storedIds && storedIds.length && storedIds.includes(this.candidate?.id);
    if (isInStoredList) {
      this.frozenNeighbourIds = storedIds;
      const index = this.frozenNeighbourIds.indexOf(this.candidate.id);
      const distanceFromEnd = this.frozenNeighbourIds.length - 1 - index;
      if (distanceFromEnd <= 20) {
        this.maybePrefetch();
      }
      return;
    }
    this.fetchCandidates();
    if (this.frozenNeighbourIds && this.frozenNeighbourIds.length) {
      this.setFrozenNeighbourIds({
        vacancyId: this.vacancy.id,
        ids: this.frozenNeighbourIds,
      });
      const index = this.frozenNeighbourIds.indexOf(this.candidate.id);
      const distanceFromEnd = this.frozenNeighbourIds.length - 1 - index;
      if (distanceFromEnd <= 20) {
        this.maybePrefetch();
      }
    }
  },
  methods: {
    ...mapMutations('candidatePage', ['setFrozenNeighbourIds', 'setPrefetchedAtLength']),
    fetchCandidates() {
      if (this.frozenNeighbourIds) return;
      const list = Candidate.query()
        .with('photos')
        .where('vacancyId', this.vacancy.id)
        .where('status', this.candidate.status)
        .orderBy('id', 'desc')
        .all();
      this.frozenNeighbourIds = list.map((c) => c.id);
    },
    getOrmNeighbourIds() {
      return Candidate.query()
        .with('photos')
        .where('vacancyId', this.vacancy.id)
        .where('status', this.candidate.status)
        .orderBy('id', 'desc')
        .all()
        .map((c) => c.id);
    },
    maybePrefetch() {
      const prefetchedAt = this.getPrefetchedAtLength(this.vacancy?.id);
      if (this.frozenNeighbourIds.length <= prefetchedAt) return;
      this.loadMoreNeighbours();
    },
    async loadMoreNeighbours() {
      const existingSet = new Set(this.frozenNeighbourIds);

      const ormIds = this.getOrmNeighbourIds();
      const ormNew = ormIds.filter((id) => !existingSet.has(id));
      if (ormNew.length) {
        this.frozenNeighbourIds = [...this.frozenNeighbourIds, ...ormNew];
        this.setFrozenNeighbourIds({ vacancyId: this.vacancy.id, ids: this.frozenNeighbourIds });
        this.setPrefetchedAtLength({ vacancyId: this.vacancy.id, length: this.frozenNeighbourIds.length });
        return;
      }

      if (!this.hasMoreData || this.isLoadingNextPage) return;
      this.isLoadingMore = true;
      try {
        const hasData = await this.prefetchNextPage(this.vacancy.id);
        if (!hasData) return;

        const apiNew = this.getOrmNeighbourIds().filter((id) => !existingSet.has(id));
        if (apiNew.length) {
          this.frozenNeighbourIds = [...this.frozenNeighbourIds, ...apiNew];
          this.setFrozenNeighbourIds({ vacancyId: this.vacancy.id, ids: this.frozenNeighbourIds });
          this.setPrefetchedAtLength({ vacancyId: this.vacancy.id, length: this.frozenNeighbourIds.length });
        }
      } finally {
        this.isLoadingMore = false;
      }
    },
  },
};
</script>
